import { lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

import { AuthLayout } from 'src/components/AuthLayout';
import { Loadable } from 'src/components/Loadable';
import { MainLayout } from 'src/components/MainLayout';
import LoadingScreen from 'src/screens/Loading';
import SsoCallback from 'src/screens/SsoCallback';
import { AuthRequired } from 'src/services/auth/AuthRequired';
import { RoleGuard, RoleGuardLayout } from 'src/services/user/RoleGuard';
import EditWorkBetterAdmin from '../screens/EditWorkBetterAdmin';
import { getRolesForPath } from './utils';

const LazyLoadableScreen = (screen: () => any) =>
  Loadable(lazy(screen), <LoadingScreen />);

const AcceptPrivacyPolicyScreen = LazyLoadableScreen(
  () => import('src/screens/AcceptPrivacyPolicy'),
);

const CollectionScreen = LazyLoadableScreen(
  () => import('src/screens/Collection'),
);

const AddNewFirmScreen = LazyLoadableScreen(
  () => import('src/screens/AddNewFirm'),
);
const AddNewUserScreen = LazyLoadableScreen(
  () => import('src/screens/AddNewUser'),
);
const AddNewVideoScreen = LazyLoadableScreen(
  () => import('src/screens/AddNewVideo'),
);
const EditUserScreen = LazyLoadableScreen(() => import('src/screens/EditUser'));
const EditVideoScreen = LazyLoadableScreen(
  () => import('src/screens/EditVideo'),
);
const HomeScreen = LazyLoadableScreen(() => import('src/screens/Home'));
const LogInScreen = LazyLoadableScreen(() => import('src/screens/LogIn'));
const ManageFirmScreen = LazyLoadableScreen(
  () => import('src/screens/ManageFirm'),
);
const ManageFirmsScreen = LazyLoadableScreen(
  () => import('src/screens/ManageFirms'),
);
const ManageUsersScreen = LazyLoadableScreen(
  () => import('src/screens/ManageUsers'),
);
const ManageVideosScreen = LazyLoadableScreen(
  () => import('src/screens/ManageVideos'),
);
const MyFavoritesScreen = LazyLoadableScreen(
  () => import('src/screens/MyFavorites'),
);
const NewPasswordScreen = LazyLoadableScreen(
  () => import('src/screens/NewPassword'),
);
const ResetPasswordScreen = LazyLoadableScreen(
  () => import('src/screens/ResetPassword'),
);
const SettingsScreen = LazyLoadableScreen(() => import('src/screens/Settings'));
const SharedWithMeScreen = LazyLoadableScreen(
  () => import('src/screens/SharedWithMe'),
);
const StatisticsScreen = LazyLoadableScreen(
  () => import('src/screens/Statistics'),
);

const AddNewAdminScreen = LazyLoadableScreen(
  () => import('src/screens/AddWorkBetterAdmin'),
);

const EditCollectionScreen = LazyLoadableScreen(
  () => import('src/screens/EditCollection'),
);

const AddNewCollectionScreen = LazyLoadableScreen(
  () => import('src/screens/AddNewCollection'),
);

const ManageCollectionsScreen = LazyLoadableScreen(
  () => import('src/screens/ManageCollections'),
);

const CollectionsScreen = LazyLoadableScreen(
  () => import('src/screens/Collections'),
);

const NotFoundScreen = LazyLoadableScreen(() => import('src/screens/NotFound'));

const OneVideo = LazyLoadableScreen(() => import('src/screens/OneVideo'));

export const Navigation = () => (
  <BrowserRouter>
    <ScrollToTop />
    <Routes>
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<LogInScreen />} />
        <Route path="reset-password" element={<ResetPasswordScreen />} />
        <Route index element={<Navigate to="login" replace />} />
      </Route>
      <Route path="/auth/sso" element={<SsoCallback />} />
      <Route path="/privacy-policy" element={<AcceptPrivacyPolicyScreen />} />
      <Route path="/change-password" element={<NewPasswordScreen />} />
      <Route path="/change-password/:token" element={<NewPasswordScreen />} />
      <Route
        path="/"
        element={
          <AuthRequired>
            <MainLayout />
          </AuthRequired>
        }
      >
        <Route index element={<HomeScreen />} />
        <Route
          path="manage-firms"
          element={
            <RoleGuardLayout
              placeholder={<Navigate to="/" replace />}
              roles={getRolesForPath('/manage-firms/*')}
            />
          }
        >
          <Route path=":firmId" element={<ManageFirmScreen />} />
          <Route path="add-new-firm" element={<AddNewFirmScreen />} />
          <Route path="users">
            <Route path="add-new-user" element={<AddNewUserScreen />} />
            <Route path="edit-user/:userId" element={<EditUserScreen />} />
            <Route index element={<ManageUsersScreen />} />
          </Route>
          <Route
            index
            element={
              <RoleGuard
                placeholder={<Navigate to="/" replace />}
                roles={getRolesForPath('/manage-firms')}
              >
                <ManageFirmsScreen />
              </RoleGuard>
            }
          />
        </Route>
        <Route
          path="manage-videos"
          element={
            <RoleGuardLayout
              placeholder={<Navigate to="/" />}
              roles={getRolesForPath('/manage-videos')}
            />
          }
        >
          <Route path="add-new-video" element={<AddNewVideoScreen />} />
          <Route path="edit-video/:videoId" element={<EditVideoScreen />} />
          <Route index element={<ManageVideosScreen />} />
        </Route>
        <Route
          path="manage-collections"
          element={
            <RoleGuardLayout
              placeholder={<Navigate to="/" />}
              roles={getRolesForPath('/manage-collections')}
            />
          }
        >
          <Route path="add-new-collection" element={<AddNewCollectionScreen />} />
          <Route path="edit-collection/:collectionId" element={<EditCollectionScreen />} />
          <Route index element={<ManageCollectionsScreen />} />
        </Route>
        <Route path="collections" element={<CollectionsScreen />} />
        <Route path="collections/:collectionId" element={<CollectionScreen />} />
        <Route
          path="my-favorites"
          element={
            <RoleGuard
              disableForSuperAdmin
              placeholder={<Navigate to="/" replace />}
              roles={getRolesForPath('/my-favorites')}
            >
              <MyFavoritesScreen />
            </RoleGuard>
          }
        />
        <Route
          path="shared-with-me"
          element={
            <RoleGuard
              disableForSuperAdmin
              placeholder={<Navigate to="/" replace />}
              roles={getRolesForPath('/shared-with-me')}
            >
              <SharedWithMeScreen />
            </RoleGuard>
          }
        />

        <Route
          path="settings"
          element={
            <RoleGuardLayout
              placeholder={<Navigate to="/" />}
              roles={getRolesForPath('/settings')}
            />
          }
        >
          <Route path="add-new-admin" element={<AddNewAdminScreen />} />
          <Route path="edit-admin/:adminId" element={<EditWorkBetterAdmin />} />
          <Route index element={<SettingsScreen />} />
        </Route>
        <Route
          path="statistics"
          element={
            <RoleGuardLayout
              placeholder={<Navigate to="/" />}
              roles={getRolesForPath('/statistics')}
            />
          }
        >
          <Route index element={<StatisticsScreen />} />
        </Route>
        <Route
          path="video"
          element={
            <RoleGuardLayout
              placeholder={<Navigate to="/" />}
              roles={getRolesForPath('/video/*')}
            />
          }
        >
          <Route path=":videoId/:goToBack" element={<OneVideo />} />
          <Route path=":videoId" element={<OneVideo />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  </BrowserRouter>
);
